/**
 * Tenant Context Switcher Component
 *
 * Allows Super Admins to switch between tenant contexts to view
 * and manage data for different organizations.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2,
  ChevronDown,
  Check,
  Globe,
  RefreshCw,
  Shield,
  X,
} from 'lucide-react';

interface TenantContextSwitcherProps {
  onTenantChange?: (tenantSlug: string | null) => void;
  compact?: boolean;
}

const TenantContextSwitcher: React.FC<TenantContextSwitcherProps> = ({
  onTenantChange,
  compact = false,
}) => {
  const { user } = useAuth();
  const {
    availableTenants,
    selectedTenantSlug,
    isSuperAdminMode,
    switchToTenant,
    switchToSystemView,
    refreshTenantList,
    isSwitching,
  } = useTenant();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Only show for super admins
  const isSuperAdmin = user?.role === 'super_admin';

  // Filter tenants based on search
  const filteredTenants = availableTenants.filter(tenant =>
    tenant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get current tenant name
  const currentTenantName = isSuperAdminMode
    ? availableTenants.find(t => t.slug === selectedTenantSlug)?.name || selectedTenantSlug
    : 'System View';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle tenant selection
  const handleSelectTenant = async (tenantSlug: string) => {
    await switchToTenant(tenantSlug);
    setIsOpen(false);
    setSearchQuery('');
    onTenantChange?.(tenantSlug);
  };

  // Handle return to system view
  const handleSystemView = () => {
    switchToSystemView();
    setIsOpen(false);
    setSearchQuery('');
    onTenantChange?.(null);
  };

  // Don't render if not super admin
  if (!isSuperAdmin) {
    return null;
  }

  if (compact) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            isSuperAdminMode
              ? 'bg-purple-100 text-purple-700 border border-purple-300'
              : 'bg-gray-100 text-gray-700 border border-gray-300'
          }`}
          title={isSuperAdminMode ? `Viewing: ${currentTenantName}` : 'System View'}
        >
          {isSuperAdminMode ? (
            <Building2 className="w-4 h-4" />
          ) : (
            <Globe className="w-4 h-4" />
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
            <TenantDropdownContent
              filteredTenants={filteredTenants}
              selectedTenantSlug={selectedTenantSlug}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleSelectTenant={handleSelectTenant}
              handleSystemView={handleSystemView}
              refreshTenantList={refreshTenantList}
              isSwitching={isSwitching}
              isSuperAdminMode={isSuperAdminMode}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Main Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all w-full ${
          isSuperAdminMode
            ? 'bg-purple-50 border-2 border-purple-400 text-purple-800'
            : 'bg-gray-50 border-2 border-gray-300 text-gray-700 hover:border-gray-400'
        } ${isSwitching ? 'opacity-50 cursor-wait' : ''}`}
      >
        <div className={`p-2 rounded-full ${isSuperAdminMode ? 'bg-purple-200' : 'bg-gray-200'}`}>
          {isSuperAdminMode ? (
            <Building2 className="w-5 h-5 text-purple-700" />
          ) : (
            <Shield className="w-5 h-5 text-gray-600" />
          )}
        </div>

        <div className="flex-1 text-left">
          <div className="text-xs text-gray-500 uppercase tracking-wide">
            {isSuperAdminMode ? 'Viewing Tenant' : 'Super Admin Mode'}
          </div>
          <div className="font-semibold truncate">
            {isSwitching ? 'Switching...' : currentTenantName}
          </div>
        </div>

        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Super Admin Mode Indicator */}
      {isSuperAdminMode && (
        <div className="mt-2 flex items-center justify-between px-3 py-2 bg-purple-100 rounded-lg">
          <span className="text-xs text-purple-700">
            Viewing tenant data as Super Admin
          </span>
          <button
            onClick={handleSystemView}
            className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Exit
          </button>
        </div>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          <TenantDropdownContent
            filteredTenants={filteredTenants}
            selectedTenantSlug={selectedTenantSlug}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleSelectTenant={handleSelectTenant}
            handleSystemView={handleSystemView}
            refreshTenantList={refreshTenantList}
            isSwitching={isSwitching}
            isSuperAdminMode={isSuperAdminMode}
          />
        </div>
      )}
    </div>
  );
};

// Dropdown content component
interface TenantDropdownContentProps {
  filteredTenants: any[];
  selectedTenantSlug: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSelectTenant: (slug: string) => void;
  handleSystemView: () => void;
  refreshTenantList: () => Promise<void>;
  isSwitching: boolean;
  isSuperAdminMode: boolean;
}

const TenantDropdownContent: React.FC<TenantDropdownContentProps> = ({
  filteredTenants,
  selectedTenantSlug,
  searchQuery,
  setSearchQuery,
  handleSelectTenant,
  handleSystemView,
  refreshTenantList,
  isSwitching,
  isSuperAdminMode,
}) => {
  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-gray-800">Switch Tenant Context</h3>
          <button
            onClick={() => refreshTenantList()}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
            title="Refresh tenant list"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search tenants..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      {/* System View Option */}
      <div className="p-2 border-b border-gray-100">
        <button
          onClick={handleSystemView}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
            !isSuperAdminMode
              ? 'bg-blue-50 text-blue-700'
              : 'hover:bg-gray-50 text-gray-700'
          }`}
        >
          <Globe className="w-5 h-5" />
          <div className="flex-1 text-left">
            <div className="font-medium">System View</div>
            <div className="text-xs text-gray-500">View all tenants data</div>
          </div>
          {!isSuperAdminMode && <Check className="w-5 h-5 text-blue-600" />}
        </button>
      </div>

      {/* Tenant List */}
      <div className="max-h-64 overflow-y-auto p-2">
        {filteredTenants.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm">
            No tenants found
          </div>
        ) : (
          filteredTenants.map((tenant) => (
            <button
              key={tenant.id}
              onClick={() => handleSelectTenant(tenant.slug)}
              disabled={isSwitching}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                selectedTenantSlug === tenant.slug
                  ? 'bg-purple-50 text-purple-700'
                  : 'hover:bg-gray-50 text-gray-700'
              } ${isSwitching ? 'opacity-50' : ''}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                tenant.is_active ? 'bg-gradient-to-br from-blue-500 to-purple-600' : 'bg-gray-400'
              }`}>
                {tenant.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium">{tenant.name}</div>
                <div className="text-xs text-gray-500">
                  {tenant.slug} {tenant.plan && `• ${tenant.plan}`}
                </div>
              </div>
              {selectedTenantSlug === tenant.slug && (
                <Check className="w-5 h-5 text-purple-600" />
              )}
              {!tenant.is_active && (
                <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-600 rounded">
                  Inactive
                </span>
              )}
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
        <p className="text-xs text-gray-500">
          {filteredTenants.length} tenant{filteredTenants.length !== 1 ? 's' : ''} available
        </p>
      </div>
    </>
  );
};

export default TenantContextSwitcher;
